import React, { useEffect, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { defineValue, taxonomyByKey, type TaxonomyEntry } from '../types/taxonomy';

// Codebook definitions on the case form, two ways:
//  - DefinitionHover wraps a pill: hovering or focusing it shows the formatted definition card.
//  - GlossaryDrawer is a non-modal side panel listing every code of one list, for reading and
//    comparing while tagging (and for touch screens, where hover does not exist).
// The case page keeps plain hover titles (Jan's call, 2026-09-25).

const labelFor = (listKey: string, code: string) => defineValue(listKey, code)?.label || code;

export const DefinitionBody: React.FC<{ listKey: string; entry: TaxonomyEntry; compact?: boolean }> = ({ listKey, entry, compact }) => (
  <div className={compact ? 'text-xs' : 'text-sm'}>
    <p className="leading-snug text-gray-700 dark:text-gray-300">{entry.definition}</p>
    {entry.counts && <p className="mt-1 leading-snug text-gray-600 dark:text-gray-400"><span className="font-medium text-gray-500 dark:text-gray-500">Counts: </span>{entry.counts}</p>}
    {entry.notCounts && <p className="mt-1 leading-snug text-gray-600 dark:text-gray-400"><span className="font-medium text-gray-500 dark:text-gray-500">Does not count: </span>{entry.notCounts}</p>}
    {entry.notToBeConfusedWith && entry.notToBeConfusedWith.length > 0 && (
      <p className="mt-1 leading-snug text-gray-600 dark:text-gray-400"><span className="font-medium text-gray-500 dark:text-gray-500">Not to be confused with: </span>{entry.notToBeConfusedWith.map((c) => labelFor(listKey, c)).join(', ')}</p>
    )}
  </div>
);

// ---- hover card --------------------------------------------------------------------------------

export const DefinitionHover: React.FC<{ listKey: string; value: string; children: React.ReactNode }> = ({ listKey, value, children }) => {
  const entry = defineValue(listKey, value);
  const [open, setOpen] = useState(false);
  const [flip, setFlip] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const timer = useRef<number | null>(null);

  const show = () => {
    if (timer.current) window.clearTimeout(timer.current);
    // A short delay so sweeping the pointer across a row does not flash a card per pill.
    timer.current = window.setTimeout(() => {
      const r = ref.current?.getBoundingClientRect();
      setFlip(!!r && r.left + 320 > window.innerWidth);
      setOpen(true);
    }, 180);
  };
  const hide = () => { if (timer.current) window.clearTimeout(timer.current); timer.current = null; setOpen(false); };
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  if (!entry) return <>{children}</>;
  return (
    <span ref={ref} className="relative inline-flex" onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
      {children}
      {open && (
        <span role="tooltip"
          className={`absolute top-full mt-1.5 z-30 w-80 max-w-[85vw] rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg p-3 text-left pointer-events-none ${flip ? 'right-0' : 'left-0'}`}>
          <span className="block text-xs font-semibold text-gray-900 dark:text-gray-100 mb-1">{entry.label}</span>
          <DefinitionBody listKey={listKey} entry={entry} compact />
        </span>
      )}
    </span>
  );
};

// ---- glossary drawer ---------------------------------------------------------------------------

export const GlossaryDrawer: React.FC<{ listKey: string | null; onClose: () => void; selected?: string[] }> = ({ listKey, onClose, selected = [] }) => {
  const list = listKey ? taxonomyByKey(listKey) : undefined;
  useEffect(() => {
    if (!list) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [list, onClose]);
  if (!list) return null;

  const groups = list.groups && list.groups.length
    ? list.groups.map((g) => ({ label: g.label, note: g.note, codes: g.values }))
    : [{ label: '', note: undefined, codes: Object.keys(list.entries) }];

  return (
    <aside aria-label={`${list.title} definitions`}
      className="fixed inset-y-0 right-0 z-40 w-[26rem] max-w-full bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col">
      <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{list.title}</h2>
          {list.rule && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{list.rule}</p>}
        </div>
        <button type="button" onClick={onClose} aria-label="Close definitions"
          className="p-1 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
        {groups.map((g) => (
          <section key={g.label || 'all'}>
            {g.label && <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">{g.label}</h3>}
            {g.note && <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{g.note}</p>}
            <dl className="space-y-3">
              {g.codes.map((code) => {
                const entry = list.entries[code];
                if (!entry) return null;
                const on = selected.includes(code);
                return (
                  <div key={code} id={`gloss-${listKey}-${code}`} className={`rounded-md -mx-2 px-2 py-1.5 ${on ? 'bg-gray-100 dark:bg-gray-800' : ''}`}>
                    <dt className="text-sm font-semibold text-gray-900 dark:text-gray-100">{on ? '✓ ' : ''}{entry.label}</dt>
                    <dd className="mt-0.5"><DefinitionBody listKey={listKey!} entry={entry} /></dd>
                  </div>
                );
              })}
            </dl>
          </section>
        ))}
      </div>
    </aside>
  );
};
